/**
 * Application Keywords Constants
 * Centralized constants for all hardcoded strings/keywords to avoid magic strings throughout the application
 */

/**
 * Period Types
 */
export const PERIOD_TYPES = {
  DAILY: "DAILY",
  MONTHLY: "MONTHLY",
  QUARTERLY: "QUARTERLY",
  YEARLY: "YEARLY"
};

/**
 * Period Labels (for display)
 */
export const PERIOD_LABELS = {
  DAILY: "By day",
  MONTHLY: "By month",
  QUARTERLY: "By quarter",
  YEARLY: "By year"
};

/**
 * Period Types Array for dropdowns
 */
export const PERIOD_TYPES_ARRAY = [
  { label: PERIOD_LABELS.YEARLY, value: PERIOD_TYPES.YEARLY },
  { label: PERIOD_LABELS.QUARTERLY, value: PERIOD_TYPES.QUARTERLY },
  { label: PERIOD_LABELS.MONTHLY, value: PERIOD_TYPES.MONTHLY },
  { label: PERIOD_LABELS.DAILY, value: PERIOD_TYPES.DAILY }
];

/**
 * Account Type Names
 */
export const ACCOUNT_TYPES = {
  STATISTIC: "Statistic",
  WAGES: "Wages"
};

/**
 * Detailed Income Statement view period.
 * Monthly = 12 month columns + Total; Quarterly = 4 quarter columns + Total.
 */
export const DIS_VIEW_PERIOD = {
  MONTHLY: "Monthly",
  QUARTERLY: "Quarterly",
};

/**
 * Detailed Income Statement-V2 (report type 25) plan-type sentinel.
 * BEST_CASE is sent as `planTypeName` to defer plan selection to the backend Best-Case
 * resolve rule (published Forecast → published Budget); the Plan dropdown is hidden for it,
 * and no planId is sent.
 */
export const CUSTOM_DIS_PLAN_TYPE = {
  BEST_CASE: "BEST_CASE",
};

/**
 * Maximum number of properties allowed for a live (in-browser) report download.
 * Above this count the live `export-report` request risks timing out on large data, so the
 * Download button is disabled and the user is directed to the "Send on mail" option instead.
 */
export const MAX_DIRECT_DOWNLOAD_PROPERTIES = 10;

/**
 * Detailed Income Statement-V2 (report type 25) is a large, multi-series report, so its live-download
 * property limit is lower than the general one. Above this count the Download button is disabled and the
 * user is directed to "Send on mail".
 */
export const MAX_DIS_DOWNLOAD_PROPERTIES = 5;

/**
 * Plan Status Types
 */
export const PLAN_STATUS = {
  DRAFT: "draft",
  PUBLISHED: "published",
  FINAL: "final",
  BUDGET: "budget",
  FORECAST: "forecast",
  WHAT_IF: "what if",
  OVERDUE: "overdue",
  REACHING_SOON: "reaching soon",
  ENOUGH_TIME: "enough time"
};

/**
 * Plan Initialization Status Types
 */
export const PLAN_INIT_STATUS = {
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  PROCESSING: "PROCESSING",
  IN_PROGRESS: "IN_PROGRESS",
  NOT_STARTED: "NOT_STARTED",
  UNKNOWN: "UNKNOWN",
  /** No init job / status record for this plan — treat as ready to open plan details. */
  NOT_FOUND: "NOT_FOUND",
};

/**
 * Job/Process Status Types
 */
export const JOB_STATUS = {
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  PROCESSING: "PROCESSING",
  IN_PROGRESS: "IN_PROGRESS"
};

/**
 * Month Names (abbreviated)
 */
export const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

/**
 * Month Names (full)
 */
export const MONTH_NAMES_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

/**
 * Standard Hours Period Types
 */
export const STANDARD_HOURS_PERIOD = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month"
};

/**
 * Standard Hours Period Array for dropdowns
 */
export const STANDARD_HOURS_PERIOD_ARRAY = [
  { label: "Day", value: STANDARD_HOURS_PERIOD.DAY },
  { label: "Week", value: STANDARD_HOURS_PERIOD.WEEK },
  { label: "Month", value: STANDARD_HOURS_PERIOD.MONTH }
];

/**
 * Toast Message Types
 */
export const TOAST_TYPES = {
  SUCCESS: "success",
  ERROR: "error",
  WARNING: "warning",
  INFO: "info"
};

/**
 * MCR Excel Upload Modes
 */
export const UPLOAD_MODES = {
  MISSING_DRIVER: "MISSING_DRIVER",
  DO_NOT_USE: "DO_NOT_USE",
  ROUNDING_CORRECTION: "ROUNDING_CORRECTION",
};

/**
 * Paired-daily dedup correction utility
 */
export const PAIRED_DAILY_DEDUP_MODES = {
  DRY_RUN: "DRY_RUN",
  DIRECT: "DIRECT",
};

export const PAIRED_DAILY_DEDUP_DECISIONS = {
  SKIP: "SKIP",
  SET_ON_DAILY: "SET_ON_DAILY",
};

export const PAIRED_DAILY_DEDUP_BUCKETS = {
  ALREADY_CLEAN: "ALREADY_CLEAN",
  LATEST_ALREADY_CORRECT: "LATEST_ALREADY_CORRECT",
  CORRECTED: "CORRECTED",
  MONTHLY_MISMATCH_NEEDS_DECISION: "MONTHLY_MISMATCH_NEEDS_DECISION",
  MULTIPLE_CONSISTENT_GENERATIONS: "MULTIPLE_CONSISTENT_GENERATIONS",
  NO_CONSISTENT_PAIR: "NO_CONSISTENT_PAIR",
};

export const PAIRED_DAILY_DEDUP_BUCKET_LABELS = {
  ALREADY_CLEAN: "Already clean",
  LATEST_ALREADY_CORRECT: "Latest already correct",
  CORRECTED: "Corrected",
  MONTHLY_MISMATCH_NEEDS_DECISION: "Monthly mismatch — needs decision",
  MULTIPLE_CONSISTENT_GENERATIONS: "Multiple consistent generations — review",
  NO_CONSISTENT_PAIR: "No consistent pair",
};

/**
 * HTTP Status Codes (commonly used)
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500
};

/**
 * Common String Values
 */
export const COMMON_STRINGS = {
  UNKNOWN: "Unknown",
  ALL: "all",
  TRUE: "true",
  FALSE: "false"
};

/**
 * Multi-line bulk value update (Update values → Manually)
 */
export const BULK_VALUE_UPDATE_STRINGS = {
  TOOLBAR_BULK_UPDATE: "Bulk value update",
  UPDATE_VALUES: "Update values",
  VIA_IMPORT: "Via import",
  MANUALLY: "Manually",
  MODAL_TITLE: "Bulk value update",
  CANCEL: "Cancel",
  SAVE: "Update values",
  HIDE_ZERO_ROWS: "Hide '0' rows",
  ADJUSTMENTS: "Adjustments",
  SHOW_COMPARISON: "Comparisons",
  ADJUSTMENT_LABEL: "Adjustment",
  REVENUE_LABEL: "Total",
  COMPARISON_LABEL: "Comparison",
  NO_DATA: "No data available.",
  EDIT_IN_VIEW_HINT: "Edit in {period} view",
  BANNER_UNSUPPORTED:
    "Only “Input By” and “Paired driver” line items are supported for line items value bulk update. Any other selected line items are not supported and will be skipped.",
  TOAST_MIXED_PERIOD:
    "Select line items with the same value period — all monthly, all quarterly, all yearly, or all daily — to update values together.",
  TOAST_NO_ELIGIBLE:
    "None of the selected rows use a supported driver. Only Input by and Paired driver can be updated manually.",
  TOAST_MAX_ROWS:
    "You can update up to {max} line items at a time. Reduce your selection and try again.",
  TOAST_NO_SELECTION: "Select at least one line item to update values manually.",
  TOAST_SAVE_SUCCESS: "Values updated successfully.",
  TOAST_SAVE_ERROR: "Failed to update values. Please try again.",
  TOAST_VALIDATION: "Resolve invalid values before saving.",
  TOAST_NO_EDITABLE: "No editable lines to save.",
  SAVING_MESSAGE: "Updating values...",
  SAVING_MESSAGE_DEPENDENTS: "Updating dependent line-items — just a moment…",
  SAVING_MESSAGE_DRAFT: "Creating a draft for updated line-items…",
}

/** Max line items that can be updated in one manual bulk session. */
export const BULK_VALUE_UPDATE_MAX_ROWS = 4000;

export const COA_SORT_ORDER_SOURCE = {
  TEMPLATE: "OTELIER_STRUCTURE_TEMPLATE",
  PREVIEW: "COA_DRAFT_PREVIEW",
};

export const BULK_CLOSED_CONFIRM = {
  HEADING: "This bulk value update includes some closed months. Are you sure you want to update the closed months' values?",
  BTN_CANCEL: "Cancel",
  BTN_UNCLOSED_ONLY: "No, update only unclosed month values",
  BTN_UPDATE_ALL: "Yes, update all",
};

export const DRAG_DROP_CLOSED_CONFIRM = {
  HEADING: "The target card(s) contain one or more closed months. Do you want to override the values for those closed months?",
  HEADING_DAILY: "One or more days in the selected range fall within a closed month. Do you want to override those values?",
  BTN_CANCEL: "Cancel",
  BTN_UNCLOSED_ONLY: "No, override only unclosed month values",
  BTN_OVERRIDE_ALL: "Yes, override all",
};

export const CLOSED_MONTH_DIALOG = {
  HEADER_PREFIX: "Last closed month:",
  BODY_DEFAULT: "Are you sure you want to update closed month values?",
  BODY_DAILY: "This day is in a closed month. Are you sure you want to update this value?",
  BODY_QUARTER_SUFFIX: "Are you sure you want to update values for closed months?",
  BODY_YEAR_SUFFIX: "Are you sure you want to update values for closed months?",
  BODY_ALL_MONTHS: "all months",
  ARIA_LABEL_NO: "No, cancel edit",
  ARIA_LABEL_YES: "Yes, proceed with edit",
};

/**
 * Plan multi-comparison (TPX-1903)
 * Up to MAX_COMPARISONS comparisons can be configured per plan. Each comparison is keyed across all
 * surfaces by its `planComparisonName` (the backend-verified unique key — the response `id` is the
 * plan being compared, identical on every entry, and is NOT a per-comparison identifier).
 */
export const MAX_COMPARISONS = 3;

/** Default name seeded into a new comparison row: "Comparison 1", "Comparison 2", ... */
export const COMPARISON_DEFAULT_NAME_PREFIX = "Comparison";

/**
 * Configure-comparison target types (UI radio → derived `dataType` sent to the backend).
 * "Specific plan" saves as dataType "Plan"; Actuals and Snapshot save as-is.
 */
export const COMPARISON_TARGET = {
  ACTUALS: "actuals",
  SPECIFIC: "specific",
  SNAPSHOT: "snapshot",
};

export const COMPARISON_DATA_TYPE = {
  ACTUALS: "Actuals",
  PLAN: "Plan",
  SNAPSHOT: "Snapshot",
};

/** Placement checkbox labels — exact Figma wording (TPX-1903). */
export const COMPARISON_PLACEMENT_LABELS = {
  HIGHLIGHTS: 'Show in "Plan highlights"',
  ROLLUPS: 'Show in "Roll-up"',
  PLAN_TABLE: "Show in Plan table",
};

/** Display-toggle labels (single global choice: numerical difference vs percentage). */
export const COMPARISON_DISPLAY_LABELS = {
  NUMERICAL: "Show as numerical difference",
  PERCENTAGE: "Show as percentage",
};

/** Plan-details grid comparison group child-column headers. */
export const COMPARISON_COLUMN_HEADERS = {
  COMP: "Comp",
  VAR: "Var",
  VAR_PERCENT: "Var %",
};

export const ACCOUNT_GROUPS_MAX_DEPTH = 20;
export const ACCOUNT_GROUPS_NEW_GROUP_BASE_NAME = "New Group";
export const ACCOUNT_GROUPS_DRAG_HINT_KEY = "accountGroupsDragHintDismissed";
// Default report-inclusion state for a SKELETON section / GROUPING group (false = included).
export const ACCOUNT_GROUPS_EXCLUDE_FROM_REPORT_DEFAULT = false;

export const ROW_TYPE = {
  SKELETON: "SKELETON",
  GROUPING: "GROUPING",
  COA: "COA",
};

// COA Structure Excel import (TPX-1732) — matches CustomPnlViewExcelConstants on the backend.
export const COA_IMPORT_STATUS = {
  ALL: "ALL",
  PARTIAL: "PARTIAL",
  NONE: "NONE",
};

export const COA_IMPORT_WARNING_LABELS = {
  INVALID_COA_CODE: "COA code not found",
  INVALID_PLACEMENT: "Not allowed under This section",
  UNKNOWN_SECTION: "Unknown section — could not place",
  NOT_FULLY_MAPPED: "Not mapped to a structure code — map it first",
};

export const ACCOUNT_TYPE_BADGE = {
  Revenue:            { letter: "R", color: "#22C55E", bg: "#22C55E29" },
  "Other Expenses":   { letter: "E", color: "#FF5630", bg: "#FF563029" },
  "Cost of Sales":    { letter: "C", color: "#B76E00", bg: "#FFAB0029" },
  "Payroll & Related":{ letter: "P", color: "#8E33FF", bg: "#8E33FF29" },
  Statistical:        { letter: "S", color: "#637381", bg: "#919EAB29" },
  "Other Income":     { letter: "O", color: "#2A5BA2", bg: "#2A5BA229" },
};

export const DND_ITEM_TYPE = {
  ACCOUNT_FROM_GRID: "ACCOUNT_FROM_GRID",
  GROUPING_NODE: "GROUPING_NODE",
};

// --- Account Groups drop restrictions (mirror of backend com.tpx.Constants) ---
// operatingDepartmentIds = roomsDepartmentIds(1,38) + fnbDepartmentIds(2,3,4,28) + miscDepartmentIds(10,33)
export const AG_OPERATING_DEPARTMENT_IDS = [1, 2, 3, 4, 10, 28, 33, 38];
export const AG_ACCOUNT_TYPE_IDS = {
  COST_OF_SALES: 49,
  OTHER_EXPENSES: 50,
  OTHER_INCOME: 51,
  PAYROLL_AND_RELATED: 52,
  REVENUE: 53,
  STATISTICAL: 54,
};
// Expense account types that go under Departmental Expenses for operating departments.
export const AG_EXPENSE_ACCOUNT_TYPE_IDS = [49, 50, 52];
// Root SKELETON sections that accept Statistical accounts (and only those).
export const AG_STATISTICAL_SECTIONS = ["Rooms Available", "Rooms Rented", "Statistics"];
export const AG_SECTION_OPERATING_REVENUE = "Operating Revenue";
export const AG_SECTION_DEPARTMENTAL_EXPENSES = "Departmental Expenses";
export const AG_SECTION_REPLACEMENT_RESERVE = "Replacement Reserve";
// Financial-statement-location display name that pins an account to Replacement Reserve.
export const AG_FSL_REPLACEMENT_RESERVE = "Replacement Reserve";

// Maps the placement-rules API section keys to their skeleton root `coaName`.
// Used to translate the API response into a coaName-keyed rule lookup.
export const AG_PLACEMENT_RULE_KEY_TO_SECTION = {
  roomsAvailable: "Rooms Available",
  roomsRented: "Rooms Rented",
  statistics: "Statistics",
  operatingRevenue: "Operating Revenue",
  departmentalExpenses: "Departmental Expenses",
  undistributedOperatingRevenue: "Undistributed Operating Revenue",
  undistributedOperatingExpenses: "Undistributed Operating Expenses",
  managementFees: "Management Fees",
  nonOperatingIncome: "Non-Operating Income",
  nonOperatingExpenses: "Non-Operating Expenses",
  interestDepreciationAndAmortization: "Interest, Deprecation & Amortization",
  tax: "Tax",
  replacementReserve: "Replacement Reserve",
};

